import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { configs } from '../../../../constants/configs/configs';
import { startSpinner, stopSpinner } from '../../../../actions/common/actionCreators';
import { SPINNER_MESSAGES_IDS } from '../../../../constants/common/miscellaneous';
import { api } from '../../../../modules/common-utils-services/axiosUtil';
import { formatDate } from '../../../../utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '../../../../utils/dates/dateFormats.utils';

interface IhasValidationsInput {
    value: string,
    isValidInput: boolean,
    isEmpty: boolean,
};

interface ISecurityAnswers {
    'firstName': IhasValidationsInput,
    'lastName': IhasValidationsInput,
    'birthDate': IhasValidationsInput,
    'ssn': IhasValidationsInput,
    'zipCode': IhasValidationsInput,
    'gender': string,
    'emailAddress': IhasValidationsInput,
    'phoneNumber': IhasValidationsInput,
    'county': IhasValidationsInput,
    'householdTotal': IhasValidationsInput,
    'medicaidId': IhasValidationsInput,
    'caseNumber': IhasValidationsInput,
    'isReCaptchaValid': boolean
}

import {
    EMAIL_REGEX,
    FIRST_NAME_REGEX,
    LAST_NAME_REGEX,
    DATE_OF_BIRTH,
    SSN_REGEX,
    ZIP_CODE_REGEX,
    PHONE_REGEX,
    COUNTY_REGEX,
    COUNT_REGEX,
    MEDICAID_ID_REGEX,
    NUMERIC_INPUT_REGEX,
    DIGIT_ONLY_REGEX,
    INPUT_SSN_REGEX
} from '../../../../utils/validators/regex';
import { isEmpty } from '../../../../utils/miscellaneous/objectHelpers';

export const FIELD_IDS = {
    'FIRST_NAME': 'firstName',
    'LAST_NAME': 'lastName',
    'DATE_OF_BIRTH': 'birthDate',
    'SSN': 'ssn',
    'ZIP_CODE': 'zipCode',
    'GENDER': 'gender',
    'EMAIL': 'emailAddress',
    'PHONE': 'phoneNumber',
    'COUNTY': 'county',
    'MEMBERS': 'householdTotal',
    'MEDICAID_ID': 'medicaidId',
    'APPLICATION_CASE_NUMBER': 'caseNumber'
};

export const validations = {
    [FIELD_IDS.FIRST_NAME]: FIRST_NAME_REGEX,
    [FIELD_IDS.LAST_NAME]: LAST_NAME_REGEX,
    [FIELD_IDS.DATE_OF_BIRTH]: DATE_OF_BIRTH,
    [FIELD_IDS.SSN]: SSN_REGEX,
    [FIELD_IDS.ZIP_CODE]: ZIP_CODE_REGEX,
    [FIELD_IDS.EMAIL]: EMAIL_REGEX,
    [FIELD_IDS.PHONE]: PHONE_REGEX,
    [FIELD_IDS.COUNTY]: COUNTY_REGEX,
    [FIELD_IDS.MEMBERS]: COUNT_REGEX,
    [FIELD_IDS.MEDICAID_ID]: MEDICAID_ID_REGEX,
    [FIELD_IDS.APPLICATION_CASE_NUMBER]: NUMERIC_INPUT_REGEX,
};

export const useDynamicSecurityQuestions = (randomQuestions) => {

    const [securityQuestions, setSecurityQuestions] = useState(<ISecurityAnswers><unknown>{
        [FIELD_IDS.FIRST_NAME]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        [FIELD_IDS.LAST_NAME]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        [FIELD_IDS.DATE_OF_BIRTH]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        [FIELD_IDS.SSN]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        [FIELD_IDS.EMAIL]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        [FIELD_IDS.ZIP_CODE]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        [FIELD_IDS.GENDER]: '',
        [FIELD_IDS.COUNTY]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        [FIELD_IDS.PHONE]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        [FIELD_IDS.MEMBERS]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        [FIELD_IDS.MEDICAID_ID]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        [FIELD_IDS.APPLICATION_CASE_NUMBER]: {
            value: '',
            isValidInput: true,
            isEmpty: false,
        },
        isReCaptchaValid: false
    });

    const dispatch = useDispatch();

    const submitReferralSecurity = async (reCaptchaValue, encryptedActivationId, reference) => {
        const url = configs.endpoints.SECURITY_QUESTIONS_VALIDATE;
        const params = { 'recaptchaResponse': reCaptchaValue, encryptedActivationId, reference };
        randomQuestions.forEach(labelId => {
            switch (labelId) {
                case FIELD_IDS.GENDER:
                    params[labelId] = securityQuestions[labelId];
                    break;
                case FIELD_IDS.SSN:
                    params[labelId] = securityQuestions[labelId].value.replace(INPUT_SSN_REGEX, '');
                    break;
                case FIELD_IDS.DATE_OF_BIRTH:
                    params[labelId] = formatDate(securityQuestions[labelId].value, MM_SLASH_DD_SLASH_YYYY);
                    break;
                case FIELD_IDS.PHONE:
                    params[labelId] = securityQuestions[labelId].value.replace(DIGIT_ONLY_REGEX, '');
                    break;
                default:
                    params[labelId] = securityQuestions[labelId].value;
            }
        });
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));

            const res = await api.post(url, params);

            if (
                res.status === 200 &&
                typeof res.data === 'string' &&
                res.data.includes('<!DOCTYPE html')
            ) {
                document.open();
                document.write(res.data);
                document.close();
                window.history.pushState({}, '', res.request.responseURL);
            }


            return res.data;
        } catch (error) {
            if (error?.response?.status === 400) {
                return error.response.data;
            }
            console.error('Error while validating access code:', error);
            throw new Error(error);
        } finally {
            dispatch(stopSpinner());
        }

    };

    const isValidInput = (labelId) => {
        if (labelId === FIELD_IDS.GENDER) {
            return !isEmpty(securityQuestions[labelId]);
        } else {
            return securityQuestions[labelId].isValidInput && !(securityQuestions[labelId].value.length === 0);
        }
    };

    const isSubmitEnabled = () => {
        if (randomQuestions.length === 0) {
            return false;
        }
        return randomQuestions.every(labelId => isValidInput(labelId));
    };

    const hasFirstNameWarning = securityQuestions.firstName.isEmpty || !securityQuestions.firstName.isValidInput;
    const firstNameWarning = securityQuestions.firstName.isEmpty ? 'dynamicSecurityQuestion.firstNameError1' : !securityQuestions.firstName.isValidInput ? 'dynamicSecurityQuestion.firstNameError2' : '';
    const hasLastNameWarning = securityQuestions.lastName.isEmpty || !securityQuestions.lastName.isValidInput;
    const lastNameWarning = securityQuestions.lastName.isEmpty ? 'dynamicSecurityQuestion.lastNameError1' : !securityQuestions.lastName.isValidInput ? 'dynamicSecurityQuestion.lastNameError2' : '';
    const hasDOBWarning = securityQuestions.birthDate.isEmpty || !securityQuestions.birthDate.isValidInput;
    const DOBWaning = securityQuestions.birthDate.isEmpty ? 'dynamicSecurityQuestion.dobError1' : !securityQuestions.birthDate.isValidInput ? 'dynamicSecurityQuestion.dobError2' : '';
    const hasSSNWarning = securityQuestions.ssn.isEmpty || !securityQuestions.ssn.isValidInput;
    const SSNWarning = securityQuestions.ssn.isEmpty ? 'dynamicSecurityQuestion.ssnError1' : !securityQuestions.ssn.isValidInput ? 'dynamicSecurityQuestion.ssnError2' : '';
    const hasZipCodeWarning = securityQuestions.zipCode.isEmpty || !securityQuestions.zipCode.isValidInput;
    const zipCodeWarning = securityQuestions.zipCode.isEmpty ? 'dynamicSecurityQuestion.zipCodeError1' : !securityQuestions.zipCode.isValidInput ? 'dynamicSecurityQuestion.zipCodeError2' : '';
    const hasEmailWarning = securityQuestions[FIELD_IDS.EMAIL].isEmpty || !securityQuestions[FIELD_IDS.EMAIL].isValidInput;
    const emailWarning = securityQuestions[FIELD_IDS.EMAIL].isEmpty ? 'dynamicSecurityQuestion.emailError1' : !securityQuestions[FIELD_IDS.EMAIL].isValidInput ? 'dynamicSecurityQuestion.emailError2' : '';
    const hasPhoneWarning = securityQuestions.phoneNumber.isEmpty || !securityQuestions.phoneNumber.isValidInput;
    const phoneWarning = securityQuestions.phoneNumber.isEmpty ? 'dynamicSecurityQuestion.phoneError1' : !securityQuestions.phoneNumber.isValidInput ? 'dynamicSecurityQuestion.phoneError2' : '';
    const hasCountyWarning = securityQuestions.county.isEmpty || !securityQuestions.county.isValidInput;
    const countyWarning = securityQuestions.phoneNumber.isEmpty ? 'dynamicSecurityQuestion.countyError1' : !securityQuestions.phoneNumber.isValidInput ? 'dynamicSecurityQuestion.countyError2' : '';
    const hasMembersWarning = securityQuestions[FIELD_IDS.MEMBERS].isEmpty || !securityQuestions[FIELD_IDS.MEMBERS].isValidInput;
    const membersWarning = securityQuestions[FIELD_IDS.MEMBERS].isEmpty ? 'dynamicSecurityQuestion.numberOfMembersError1' : !securityQuestions[FIELD_IDS.MEMBERS].isValidInput ? 'dynamicSecurityQuestion.numberOfMembersError2' : '';
    const hasMedicaidIdWarning = securityQuestions.medicaidId.isEmpty || !securityQuestions.medicaidId.isValidInput;
    const medicaidIdWarning = securityQuestions.medicaidId.isEmpty ? 'dynamicSecurityQuestion.medicaidIdError1' : !securityQuestions.medicaidId.isValidInput ? 'dynamicSecurityQuestion.medicaidIdError2' : '';
    const hasApplicationCaseNumberWarning = securityQuestions[FIELD_IDS.APPLICATION_CASE_NUMBER].isEmpty || !securityQuestions[FIELD_IDS.APPLICATION_CASE_NUMBER].isValidInput;
    const applicationCaseNumberWarning = securityQuestions[FIELD_IDS.APPLICATION_CASE_NUMBER].isEmpty ? 'dynamicSecurityQuestion.applicationCaseNumberError1' : !securityQuestions[FIELD_IDS.APPLICATION_CASE_NUMBER].isValidInput ? 'dynamicSecurityQuestion.applicationCaseNumberError2' : '';


    return {
        submitReferralSecurity, securityQuestions, setSecurityQuestions, isSubmitEnabled, hasFirstNameWarning, firstNameWarning, hasLastNameWarning, lastNameWarning,
        hasDOBWarning, DOBWaning, hasSSNWarning, SSNWarning,
        hasZipCodeWarning, zipCodeWarning, hasEmailWarning, emailWarning,
        hasPhoneWarning, phoneWarning, hasCountyWarning, countyWarning,
        hasMembersWarning, membersWarning, hasMedicaidIdWarning, medicaidIdWarning, hasApplicationCaseNumberWarning, applicationCaseNumberWarning
    };
};